import React, { useState, useEffect } from 'react';
import { supabase } from '../Servicios/supabaseClient';
import { useAuth } from '../Context/AuthContext';
import Swal from 'sweetalert2';

export default function ExternalAccountsModal({ onClose }) {
    const { user } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    // Estado para el formulario
    const [formData, setFormData] = useState({
        platform: 'Otros',
        reference_name: '',
        username: '',
        password: ''
    });

    // --- ESTADO NUEVO PARA EDICIÓN ---
    const [editingId, setEditingId] = useState(null);

    // --- 1. TRAER CUENTAS (GET) ---
    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('linked_accounts')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setAccounts(data);
        } catch (error) {
            console.error("Error trayendo cuentas:", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchAccounts();
    }, [user]);

    // --- FUNCIÓN PARA CARGAR DATOS EN EL FORM (Al tocar Editar) ---
    const handleEditClick = (item) => {
        setFormData({
            platform: item.platform || 'Otros',
            reference_name: item.reference_name,
            username: item.username,
            password: '' // No mostramos la contraseña por seguridad
        });
        setEditingId(item.id);
    };

    // --- FUNCIÓN PARA CANCELAR EDICIÓN ---
    const handleCancel = () => {
        setFormData({ platform: 'Otros', reference_name: '', username: '', password: '' });
        setEditingId(null);
    };

    // --- 2. CARGAR NUEVA CUENTA O EDITAR (INSERT/UPDATE) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);

        try {
            if (editingId) {
                // --- MODO ACTUALIZAR (UPDATE) ---
                const updates = {
                    reference_name: formData.reference_name,
                    username: formData.username,
                    platform: formData.platform
                };

                // Solo actualizamos la contraseña si el usuario escribió algo
                if (formData.password && formData.password.trim() !== '') {
                    updates.password_field = formData.password;
                }

                const { error } = await supabase
                    .from('linked_accounts')
                    .update(updates)
                    .eq('id', editingId);

                if (error) throw error;

                Swal.fire({
                    icon: 'success',
                    title: 'Cuenta actualizada',
                    text: 'Los datos se han actualizado correctamente.',
                    timer: 2000,
                    showConfirmButton: false
                });

            } else {
                // --- MODO CREAR (INSERT) ---
                const { error } = await supabase
                    .from('linked_accounts')
                    .insert([{
                        user_id: user.id,
                        reference_name: formData.reference_name,
                        username: formData.username,
                        platform: formData.platform,
                        password_field: formData.password
                    }]);

                if (error) throw error;

                Swal.fire({
                    icon: 'success',
                    title: 'Cuenta vinculada',
                    text: 'La cuenta se ha agregado correctamente.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }

            // Limpiar form y recargar lista
            handleCancel();
            fetchAccounts();

        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al guardar: ' + error.message
            });
        } finally {
            setCreating(false);
        }
    };

    // --- 3. BORRAR CUENTA (DELETE) ---
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: "No podrás revertir esta acción",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, desvincular',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const { error } = await supabase
                .from('linked_accounts')
                .delete()
                .eq('id', id);

            if (error) throw error;

            // Actualizar estado local eliminando la cuenta borrada
            setAccounts(accounts.filter(acc => acc.id !== id));

            // Si estábamos editando justo esa, cancelamos
            if (editingId === id) {
                handleCancel();
            }

            Swal.fire(
                'Desvinculada',
                'La cuenta ha sido eliminada.',
                'success'
            );

        } catch (error) {
            console.error("Error borrando:", error.message);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar la cuenta.'
            });
        }
    };

    return (
        <div className="modalOverlay">
            <div className="modalContent" style={{ maxWidth: '600px', width: '95%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0, color: 'white' }}>Cuentas Linkeadas</h3>
                    <button onClick={onClose} className="btnSecondary" style={{ padding: '4px 8px' }}>✕</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    {/* Columna Izquierda: Lista de Cuentas */}
                    <div>
                        <h4 style={{ color: '#8896a7', marginBottom: '1rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>Tus Cuentas</h4>

                        {loading ? (
                            <div style={{ color: '#888', fontStyle: 'italic' }}>Cargando...</div>
                        ) : accounts.length === 0 ? (
                            <div style={{ color: '#888', fontStyle: 'italic' }}>No hay cuentas vinculadas.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                                {accounts.map(acc => (
                                    <div key={acc.id} style={{
                                        background: 'rgba(255,255,255,0.05)',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        border: '1px solid #333',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: 'bold', color: '#fff' }}>{acc.reference_name}</div>
                                            <div style={{ fontSize: '0.8rem', color: '#aaa' }}>{acc.username}</div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleEditClick(acc)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#4a90e2',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2rem'
                                                }}
                                                title="Editar"
                                            >
                                                ✎
                                            </button>
                                            <button
                                                onClick={() => handleDelete(acc.id)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#ff5656',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2rem'
                                                }}
                                                title="Desvincular"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Columna Derecha: Formulario de Alta/Edición */}
                    <div style={{ borderLeft: '1px solid #333', paddingLeft: '2rem' }}>
                        <h4 style={{ color: '#8896a7', marginBottom: '1rem', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                            {editingId ? 'Editar Cuenta' : 'Vincular Nueva'}
                        </h4>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div>
                                <label className="inputLabel">Nombre de Referencia</label>
                                <input
                                    type="text"
                                    className="inputField"
                                    placeholder="Ej: Ventas Principal"
                                    value={formData.reference_name}
                                    onChange={e => setFormData({ ...formData, reference_name: e.target.value })}
                                    required
                                />
                            </div>

                            <div>
                                <label className="inputLabel">Usuario / Email</label>
                                <input
                                    type="text"
                                    className="inputField"
                                    placeholder="usuario@ejemplo.com"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    required
                                />
                            </div>

                            <div>
                                <label className="inputLabel">
                                    {editingId ? 'Nueva Contraseña (Opcional)' : 'Contraseña / Token'}
                                </label>
                                <input
                                    type="password"
                                    className="inputField"
                                    placeholder={editingId ? "Dejar vacío para mantener" : "********"}
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    required={!editingId}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                                <button type="submit" className="btnPrimary" disabled={creating} style={{ flex: 1 }}>
                                    {creating ? 'Guardando...' : (editingId ? 'Guardar Cambios' : 'Vincular Cuenta')}
                                </button>
                                {editingId && (
                                    <button
                                        type="button"
                                        className="btnSecondary"
                                        onClick={handleCancel}
                                        disabled={creating}
                                    >
                                        Cancelar
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
